<template><span /></template>
<script lang="ts">
import { useTimer } from '@getaccept/lib-shared/src/composables/timer.composable';
import { throttle } from '@getaccept/lib-shared/src/helpers';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import type { DocumentRecipientRevision } from '@getaccept/lib-shared/src/types/document-revision';
import type { PropType, Ref } from 'vue';
import { defineComponent, onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';
import TrackingService from '../../api/tracking/services/tracking.service';
import { TrackingConstants } from '../constants/tracking';
import { TrackingHelper } from '../helpers/tracking.helper';

export default defineComponent({
  name: 'TrackVisitTimes',
  props: {
    paused: {
      type: Boolean,
      required: true,
    },
    pagesInViewport: {
      type: Array as PropType<DocumentPage[]>,
      default: () => [],
    },
    latestSealedRevision: {
      type: Object as PropType<DocumentRecipientRevision>,
      default: null,
    },
  },
  setup(props) {
    const visitTimes: Ref<{
      [pageId: string]: { pageNum: number; pageTime: number };
    }> = ref({});

    const addNewPages = (pages: DocumentPage[]) => {
      pages.forEach((page: DocumentPage) => {
        if (visitTimes.value[page.id]) {
          return;
        }
        visitTimes.value[page.id] = { pageNum: page.pageNum, pageTime: 0 };
      });
    };

    const removeOldPages = (pages: DocumentPage[]) => {
      Object.keys(visitTimes.value)
        .filter(
          (pageTrackingId: string) =>
            !pages.map((page: DocumentPage) => page.id).includes(pageTrackingId)
        )
        .forEach((id: string) => delete visitTimes.value[id]);
    };

    const updatevisitTimes = (pages: DocumentPage[]) => {
      removeOldPages(pages);
      addNewPages(pages);
    };

    const { pagesInViewport, latestSealedRevision, paused } = toRefs(props);

    watch(pagesInViewport, (pages: DocumentPage[]) => {
      if (!pages?.length) {
        return;
      }
      updatevisitTimes(pages);
    });

    const { startTimer, stopTimer, elapsedTime } = useTimer();

    watch(elapsedTime, () => {
      if (paused.value || !Object.keys(visitTimes.value).length) {
        return;
      }
      Object.keys(visitTimes.value).forEach((id: string) => {
        visitTimes.value[id].pageTime++;
        throttlePostTracking();
      });
    });

    const resetVisitTimes = () => {
      Object.keys(visitTimes.value).forEach((id: string) => (visitTimes.value[id].pageTime = 0));
    };

    const postTracking = async () => {
      const payload = TrackingHelper.getVisitTimesPayload(
        visitTimes.value,
        latestSealedRevision.value
      );
      if (!payload.length) {
        return;
      }
      try {
        await TrackingService.trackVisitTimes(payload);
        resetVisitTimes();
      } catch (e) {
        console.error('Could not broadcast tracking', e);
      }
    };

    const throttlePostTracking = throttle(postTracking, TrackingConstants.postTrackingInterval);

    onMounted(() => {
      startTimer();
    });

    onBeforeUnmount(() => {
      stopTimer();
    });

    return {
      visitTimes,
      addNewPages,
      removeOldPages,
      postTracking,
    };
  },
});
</script>
